import type { AvailabilityHookCreated, AvailabilityHookView, DepositRule, DoorColor, GroupDiscountTier } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, post, put } from "../../api";
import { Alert, Button, Checkbox, Field, MoneyField, Panel, PanelHeader, SelectField, TextAreaField } from "../../components/ui";
import { ReaderBackground } from "./ReaderBackground";
import { AddButton, NumberInput, RemoveButton, SettingsForm, type SettingsResponse, useDraft } from "./common";

const PAYMENT_KEYS = [
  "booking.groupMinGuests",
  "booking.clientConfirms",
  "payments.cardOnFileClinic",
  "payments.cardOptional",
  "payments.noShowFeeCents",
  "payments.clinicDepositEnabled",
  "payments.clinicDepositCents",
  "payments.mobileDeposit",
  "payments.eventDeposit",
  "payments.tipPercents",
  "payments.cardReaderEnabled",
  "payments.checkoutStartsOn",
] as const;
const DISCOUNT_KEYS = ["pricing.groupDiscountClinic", "pricing.groupDiscountMobile", "pricing.groupDiscountEvent"] as const;
const CANCEL_KEYS = ["cancellation.windowHours", "cancellation.policyText"] as const;
const VIEW_KEYS = ["availability.viewStart", "availability.viewEnd"] as const;
const LIGHT_KEYS = ["booking.clinicMinNoticeMinutes"] as const;
const HOURS = Array.from({ length: 25 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
const hourName = (t: string) => {
  const h = Number(t.slice(0, 2));
  return h === 0 || h === 24 ? "Midnight" : h === 12 ? "Noon" : `${((h + 11) % 12) + 1} ${h < 12 ? "AM" : "PM"}`;
};

export function BookingTab({ data }: { data: SettingsResponse }) {
  const pay = useDraft(data.settings, PAYMENT_KEYS, "Payment rules saved.");
  const disc = useDraft(data.settings, DISCOUNT_KEYS, "Group discounts saved.");
  const cancel = useDraft(data.settings, CANCEL_KEYS, "Cancellation policy saved.");
  const view = useDraft(data.settings, VIEW_KEYS, "Availability hours saved.");
  const p = pay.value;

  return (
    <div className="flex flex-col gap-6">
      <BookingLinks slug={data.clinic.slug} />
      <AvailabilityLightSettings data={data} />
      <Panel>
        <PanelHeader title="Payments" description="Cards on file, deposits, tips, and the card reader." />
        <SettingsForm onSave={pay.save} saving={pay.saving} error={pay.error} notice={pay.notice} dirty={pay.dirty}>
          <div>
            <SelectField
              className="max-w-md"
              label="Clinic visits: card on file"
              hint={
                !p["payments.cardOnFileClinic"]
                  ? "No card is asked for, and there's no no-show or late-cancel fee."
                  : p["payments.cardOptional"]
                    ? "Online bookings confirm right away; after booking, clients can add a card for faster checkout. The fee policy below still applies: with a card it's charged automatically, without one their file shows the fee owed."
                    : "Online bookings add a card before they're confirmed (nothing is charged). No-shows and cancellations inside the cancellation window are charged the fee below, automatically."
              }
              value={!p["payments.cardOnFileClinic"] ? "off" : p["payments.cardOptional"] ? "optional" : "required"}
              onChange={(e) => {
                pay.set("payments.cardOnFileClinic", e.target.value !== "off");
                pay.set("payments.cardOptional", e.target.value === "optional");
              }}
            >
              <option value="required">Required</option>
              <option value="optional">Optional</option>
              <option value="off">Off</option>
            </SelectField>
            {p["payments.cardOnFileClinic"] && (
              <MoneyField className="ml-9 max-w-48" label="No-show / late-cancel fee" hint="$0 = no fee" cents={p["payments.noShowFeeCents"]} onChange={(c) => pay.set("payments.noShowFeeCents", c ?? 0)} />
            )}
          </div>
          <Checkbox
            label="Clinic visits wait for the client to confirm"
            description={'Online and staff bookings stay "Unconfirmed" until the client replies C to their booking text or reminder. The card is still saved and the fee policy still applies.'}
            checked={p["booking.clientConfirms"]}
            onChange={(on) => pay.set("booking.clientConfirms", on)}
          />
          {!p["payments.cardOnFileClinic"] && (
            <div>
              <Checkbox
                label="Clinic deposit instead"
                description="Charged at booking for clinic visits. Non-refundable."
                checked={p["payments.clinicDepositEnabled"]}
                onChange={(on) => pay.set("payments.clinicDepositEnabled", on)}
              />
              {p["payments.clinicDepositEnabled"] && (
                <MoneyField className="ml-9 max-w-48" label="Deposit amount" cents={p["payments.clinicDepositCents"]} onChange={(c) => pay.set("payments.clinicDepositCents", c ?? 0)} />
              )}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <DepositRuleEditor title="Mobile visits (held at request, charged when a nurse accepts)" value={p["payments.mobileDeposit"]} onChange={(r) => pay.set("payments.mobileDeposit", r)} />
            <DepositRuleEditor title="Events (default; each event can set its own)" value={p["payments.eventDeposit"]} onChange={(r) => pay.set("payments.eventDeposit", r)} />
            <p className="text-sm text-muted">The person booking a group pays the whole deposit. Late cancellations and no-shows keep the deposit.</p>
          </div>

          <Field
            label="A booking counts as a group at"
            type="number"
            min={2}
            max={50}
            value={p["booking.groupMinGuests"]}
            onChange={(e) => pay.set("booking.groupMinGuests", Number(e.target.value))}
            hint="Guests, including the person booking (for group discounts)."
            className="max-w-64"
          />

          <div>
            <p className="mb-1 text-sm font-semibold text-ink-soft">Suggested tips at checkout</p>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {p["payments.tipPercents"].map((t, i) => (
                <NumberInput
                  key={i}
                  label={`Tip ${i + 1}`}
                  value={t}
                  min={1}
                  max={100}
                  step={1}
                  suffix="%"
                  onChange={(n) => pay.set("payments.tipPercents", p["payments.tipPercents"].map((x, j) => (j === i ? n : x)))}
                />
              ))}
              {p["payments.tipPercents"].length > 1 && (
                <RemoveButton label="Remove the last tip" onClick={() => pay.set("payments.tipPercents", p["payments.tipPercents"].slice(0, -1))} />
              )}
              {p["payments.tipPercents"].length < 3 && (
                <AddButton onClick={() => pay.set("payments.tipPercents", [...p["payments.tipPercents"], Math.min(100, (p["payments.tipPercents"].at(-1) ?? 15) + 5)])}>Add a tip</AddButton>
              )}
            </div>
            <p className="mt-1 text-sm text-muted">
              Figured on the full amount, before discounts and deposits. Tips go to the visit's nurse. Every tip screen (checkout, the iPad, the card reader) shows these with Custom and No tip; paying the card on file on the reader shows the first two.
            </p>
          </div>

          <SelectField
            label="Checkout starts on"
            className="max-w-64"
            value={p["payments.checkoutStartsOn"]}
            onChange={(e) => pay.set("payments.checkoutStartsOn", e.target.value as typeof p["payments.checkoutStartsOn"])}
            hint="When it can't be used for a visit (no card saved, no reader or iPad), checkout starts on the card on file, else a new card."
          >
            <option value="card_on_file">Card on file</option>
            <option value="card">New card</option>
            <option value="card_present">Card reader</option>
            <option value="cash">Cash</option>
            <option value="link">Text a pay link</option>
            <option value="ipad">On the iPad</option>
          </SelectField>

          <Checkbox
            label="Card reader (tap to pay)"
            description="Take card payments on a Stripe card reader at checkout. Register readers below."
            checked={p["payments.cardReaderEnabled"]}
            onChange={(on) => pay.set("payments.cardReaderEnabled", on)}
          />
        </SettingsForm>
      </Panel>

      {p["payments.cardReaderEnabled"] && <ReadersPanel />}
      {p["payments.cardReaderEnabled"] && <ReaderBackground />}

      <Panel>
        <PanelHeader title="Group discounts" description="Percent off each guest's service by group size. Individual services can use their own discounts or none." />
        <SettingsForm onSave={disc.save} saving={disc.saving} error={disc.error} notice={disc.notice} dirty={disc.dirty}>
          <TierEditor title="Clinic" tiers={disc.value["pricing.groupDiscountClinic"]} onChange={(t) => disc.set("pricing.groupDiscountClinic", t)} />
          <TierEditor title="Mobile" tiers={disc.value["pricing.groupDiscountMobile"]} onChange={(t) => disc.set("pricing.groupDiscountMobile", t)} />
          <TierEditor title="Events" tiers={disc.value["pricing.groupDiscountEvent"]} onChange={(t) => disc.set("pricing.groupDiscountEvent", t)} />
        </SettingsForm>
      </Panel>

      <Panel>
        <PanelHeader title="Availability calendar" description="The hours the Availability page shows. It stretches to fit anyone's hours or bookings outside them." />
        <SettingsForm onSave={view.save} saving={view.saving} error={view.error} notice={view.notice} dirty={view.dirty}>
          <div className="flex flex-wrap gap-3">
            <SelectField label="From" value={view.value["availability.viewStart"]} onChange={(e) => view.set("availability.viewStart", e.target.value)}>
              {HOURS.slice(0, 24).map((t) => (
                <option key={t} value={t}>
                  {hourName(t)}
                </option>
              ))}
            </SelectField>
            <SelectField label="Until" value={view.value["availability.viewEnd"]} onChange={(e) => view.set("availability.viewEnd", e.target.value)}>
              {HOURS.slice(1).filter((t) => t > view.value["availability.viewStart"]).map((t) => (
                <option key={t} value={t}>
                  {hourName(t)}
                </option>
              ))}
            </SelectField>
          </div>
        </SettingsForm>
      </Panel>

      <Panel>
        <PanelHeader title="Cancellations and no-shows" description="Shown to clients when they book." />
        <SettingsForm onSave={cancel.save} saving={cancel.saving} error={cancel.error} notice={cancel.notice} dirty={cancel.dirty}>
          <Field
            label="Cancellation window (hours before the visit)"
            type="number"
            min={0}
            max={168}
            className="max-w-64"
            value={cancel.value["cancellation.windowHours"]}
            onChange={(e) => cancel.set("cancellation.windowHours", Number(e.target.value))}
          />
          <TextAreaField
            label="Policy"
            rows={4}
            maxLength={2000}
            value={cancel.value["cancellation.policyText"]}
            onChange={(e) => cancel.set("cancellation.policyText", e.target.value)}
          />
        </SettingsForm>
      </Panel>
    </div>
  );
}

function DepositRuleEditor({ title, value, onChange }: { title: string; value: DepositRule; onChange: (r: DepositRule) => void }) {
  return (
    <fieldset className="rounded-[var(--radius-control)] border border-line px-4 py-3">
      <legend className="px-1 text-sm font-semibold text-ink-soft">{title} deposit</legend>
      <div className="grid items-end gap-3 sm:grid-cols-4">
        <SelectField label="Figured as" value={value.mode} onChange={(e) => onChange({ ...value, mode: e.target.value as DepositRule["mode"] })}>
          <option value="percent">Percent of total</option>
          <option value="flat_per_guest">Flat per guest</option>
        </SelectField>
        {value.mode === "percent" ? (
          <Field label="Percent" type="number" min={0} max={100} value={value.percent} onChange={(e) => onChange({ ...value, percent: Number(e.target.value) })} />
        ) : (
          <MoneyField label="Per guest" cents={value.flatCents} onChange={(c) => onChange({ ...value, flatCents: c ?? 0 })} />
        )}
        <MoneyField label="Minimum" optional placeholder="None" cents={value.minCents} onChange={(c) => onChange({ ...value, minCents: c })} />
        <MoneyField label="Maximum" optional placeholder="None" cents={value.maxCents} onChange={(c) => onChange({ ...value, maxCents: c })} />
      </div>
    </fieldset>
  );
}

export function TierEditor({ title, tiers, onChange }: { title: string; tiers: GroupDiscountTier[]; onChange: (t: GroupDiscountTier[]) => void }) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-semibold text-ink-soft">{title}</legend>
      {tiers.length === 0 && <p className="text-sm text-muted">No group discount.</p>}
      {tiers.map((t, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
          <NumberInput label="Group size" value={t.minGuests} min={2} max={100} onChange={(n) => onChange(tiers.map((x, j) => (j === i ? { ...x, minGuests: n } : x)))} suffix="or more guests:" />
          <NumberInput label="Percent off" value={t.percent} min={0} max={100} step={0.5} onChange={(n) => onChange(tiers.map((x, j) => (j === i ? { ...x, percent: n } : x)))} suffix="% off" />
          <RemoveButton label="Remove tier" onClick={() => onChange(tiers.filter((_, j) => j !== i))} />
        </div>
      ))}
      <AddButton onClick={() => onChange([...tiers, { minGuests: (tiers[tiers.length - 1]?.minGuests ?? 1) + 1, percent: 10 }])}>Add tier</AddButton>
    </fieldset>
  );
}

function ReadersPanel() {
  const list = useQuery({ queryKey: ["card-readers"], queryFn: () => get<{ id: string; label: string; readerRef: string; active: boolean }[]>("/api/card-readers") });
  const [label, setLabel] = useState("");
  const [ref, setRef] = useState("");
  const add = useMutation({ mutationFn: () => post("/api/card-readers", { label, readerRef: ref }), onSuccess: () => (setLabel(""), setRef(""), void list.refetch()) });
  const remove = useMutation({ mutationFn: (id: string) => post(`/api/card-readers/${id}/remove`), onSuccess: () => void list.refetch() });
  const active = (list.data ?? []).filter((r) => r.active);
  return (
    <Panel>
      <PanelHeader title="Card readers" description="Register each Stripe reader (Stripe dashboard → Terminal → Readers) by its id, which starts with tmr_." />
      <div className="flex flex-col gap-3 px-5 pb-5">
        <ul className="flex flex-col gap-1 text-sm">
          {active.map((r) => (
            <li key={r.id} className="flex items-center gap-3">
              <span className="flex-1 font-semibold">{r.label}</span>
              <span className="font-mono text-muted">{r.readerRef}</span>
              <RemoveButton label={`Remove ${r.label}`} onClick={() => remove.mutate(r.id)} />
            </li>
          ))}
          {!active.length && <li className="text-muted">No readers yet.</li>}
        </ul>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Name" placeholder="Front desk" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Field label="Reader id" placeholder="tmr_…" value={ref} onChange={(e) => setRef(e.target.value)} />
          <Button busy={add.isPending} disabled={!label || !ref} onClick={() => add.mutate()}>
            Add reader
          </Button>
        </div>
        {add.error && <Alert>{errorMessage(add.error)}</Alert>}
      </div>
    </Panel>
  );
}

/** The public booking pages, to put on the website or send to clients. */
function BookingLinks({ slug }: { slug: string | null }) {
  const [copied, setCopied] = useState<string | null>(null);
  const links = slug
    ? [
        { label: "Clinic visits", url: `${window.location.origin}/book/${slug}` },
        { label: "Mobile visits (we come to them)", url: `${window.location.origin}/book/${slug}/mobile` },
      ]
    : [];
  return (
    <Panel>
      <PanelHeader title="Booking links" description="Where clients book online. Event links are on each event." />
      <ul className="divide-y divide-mist border-t border-line">
        {!slug && <li className="px-5 py-4 text-sm text-muted">Set a link name under Settings → Clinic first.</li>}
        {links.map((l) => (
          <li key={l.url} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">{l.label}</div>
              <a href={l.url} target="_blank" rel="noopener" className="break-all text-sm text-brand">
                {l.url}
              </a>
            </div>
            <Button variant="quiet" onClick={() => void navigator.clipboard?.writeText(l.url).then(() => setCopied(l.url))}>
              {copied === l.url ? "Copied" : "Copy"}
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** The availability light: its normal delay, and secret links other systems use to change it. */
function AvailabilityLightSettings({ data }: { data: SettingsResponse }) {
  const qc = useQueryClient();
  const d = useDraft(data.settings, LIGHT_KEYS, "Booking delay saved.");
  const links = useQuery({ queryKey: ["availability-links"], queryFn: () => get<AvailabilityHookView[]>("/api/availability-light/links") });
  const [name, setName] = useState("");
  const [made, setMade] = useState<AvailabilityHookCreated | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => post<AvailabilityHookCreated>("/api/availability-light/links", { name }),
    onSuccess: async (h) => {
      setMade(h);
      setName("");
      await qc.invalidateQueries({ queryKey: ["availability-links"] });
    },
  });
  const revoke = useMutation({ mutationFn: (id: string) => del(`/api/availability-light/links/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["availability-links"] }) });
  const door = useMutation({
    mutationFn: (v: { id: string; doorOpenColor: DoorColor | null; doorClosedColor: DoorColor | null }) => put(`/api/availability-light/links/${v.id}/door`, v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["availability-links"] }),
  });
  const copy = (url: string) => void navigator.clipboard?.writeText(url).then(() => setCopied(url));
  return (
    <Panel>
      <PanelHeader
        title="Availability light"
        description="The light at the top of every page. Normally online booking waits this many minutes before the first clinic time it offers (yellow; 0 makes it green). A nurse on shift, a manager, or an admin can set it green (book now), yellow for a different delay, or red (no online bookings) until it's changed or the day ends (with no nurse on shift it shows red on its own)."
      />
      <SettingsForm onSave={d.save} saving={d.saving} error={d.error} notice={d.notice} dirty={d.dirty}>
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold">Normal online booking delay</span>
          <NumberInput label="Normal online booking delay in minutes" min={0} max={1440} suffix="minutes" value={d.value["booking.clinicMinNoticeMinutes"]} onChange={(n) => d.set("booking.clinicMinNoticeMinutes", n)} />
        </label>
      </SettingsForm>
      <div className="flex flex-col gap-3 border-t border-line px-5 py-5 text-sm">
        <div>
          <h3 className="font-semibold">Links for other systems</h3>
          <p className="text-ink-soft">For UniFi Alarm Manager and the like: each link sets the light when it's opened (GET or POST). Name it for what triggers it, e.g. "Office door open"; that name shows as who changed the light.</p>
        </div>
        {made && (
          <div className="rounded-[var(--radius-control)] border border-ok/40 bg-ok-wash p-3">
            <p className="font-semibold">{made.name}: copy the one you need now. They won't be shown again.</p>
            <ul className="mt-2 flex flex-col gap-2">
              {(
                [
                  ["Green (book now)", made.urls.green],
                  ["Yellow, 30 minutes (change the number as needed)", made.urls.yellow],
                  ["Red (no online bookings)", made.urls.red],
                  ["Back to normal", made.urls.normal],
                ] as const
              ).map(([label, url]) => (
                <li key={url} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold">{label}</span>
                    <code className="block break-all text-xs">{url}</code>
                  </span>
                  <Button variant="quiet" onClick={() => copy(url)}>
                    {copied === url ? "Copied" : "Copy"}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <ul className="divide-y divide-mist">
          {(links.data ?? []).map((l) => (
            <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                <span className="font-semibold">{l.name}</span>
                <span className="block text-xs text-muted">
                  Made {new Date(l.createdAt).toLocaleDateString()}
                  {l.createdBy ? ` by ${l.createdBy}` : ""}
                </span>
              </span>
              <Button variant="quiet" busy={revoke.isPending} onClick={() => window.confirm(`Turn off "${l.name}"? Anything using it stops working.`) && revoke.mutate(l.id)}>
                Turn off
              </Button>
              <div className="flex basis-full flex-wrap items-end gap-2">
                {(
                  [
                    ["doorOpenColor", "Door opened is its…"],
                    ["doorClosedColor", "Door closed is its…"],
                  ] as const
                ).map(([k, label]) => (
                  <SelectField
                    key={k}
                    label={label}
                    className="w-52"
                    value={l[k] ?? ""}
                    onChange={(e) => door.mutate({ id: l.id, doorOpenColor: l.doorOpenColor, doorClosedColor: l.doorClosedColor, [k]: (e.target.value || null) as DoorColor | null })}
                  >
                    <option value="">Not the door</option>
                    <option value="green">Green address</option>
                    <option value="yellow">Yellow address</option>
                    <option value="red">Red address</option>
                    <option value="normal">Back to normal address</option>
                  </SelectField>
                ))}
              </div>
            </li>
          ))}
        </ul>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Field label="New link name" placeholder="Office door open" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
          <Button variant="secondary" type="submit" busy={create.isPending}>
            Make link
          </Button>
        </form>
        <p className="text-xs text-muted">
          For the office door's alarm, say which address it calls when the door opens and which when it closes. When the door closes after being open 5 minutes or more, each nurse on shift with hours left today gets one text that day: a link to block off the rest of the day, or, with visits still booked, their times.
        </p>
        {(create.error ?? revoke.error ?? door.error) && <Alert>{errorMessage(create.error ?? revoke.error ?? door.error)}</Alert>}
      </div>
    </Panel>
  );
}
